import Image from '@atlaskit/image';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { useMountToHtmlElement } from '@post-office/components--post-office-team';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { useMessageEvent } from '@post-office/message-lifecycle';

import { useStepContext } from '@post-office/message-step-context';

import React, { useCallback, useEffect } from 'react';

import { FormattedMessage } from 'react-intl';

import { messages } from './messages';

import { DEFAULT_TOUR, SPOTLIGHT_CARD_WIDTH } from './constants';

import DocumentationDarkImage from '../assets/documentation-dark.png';

import DocumentationImage from '../assets/documentation-light.png';

import InteractiveVideoDarkImage from '../assets/interactive-video-dark.png';

import InteractiveVideoImage from '../assets/interactive-video-light.png';

import { type MessageTemplateContext, type StepId } from '../index';

import { SpotlightStepCount } from '@atlaskit/spotlight/step-count';

import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';

import { SpotlightMedia } from '@atlaskit/spotlight/media';

import { SpotlightHeadline } from '@atlaskit/spotlight/headline';

import { SpotlightHeader } from '@atlaskit/spotlight/header';

import { SpotlightFooter } from '@atlaskit/spotlight/footer';

import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';

import { SpotlightControls } from '@atlaskit/spotlight/controls';

import { SpotlightCard } from '@atlaskit/spotlight/card';

import { SpotlightBody } from '@atlaskit/spotlight/body';

import { SpotlightActions } from '@atlaskit/spotlight/actions';

import { PopoverTarget } from '@atlaskit/spotlight/popover-target';

import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';

import { PopoverContent } from '@atlaskit/spotlight/popover-content';

import type { Placement } from '@atlaskit/spotlight/types';

type NewStepDefinition = {
	heading: keyof typeof messages;
	description: keyof typeof messages;
	lightImage: string;
	darkImage: string;
	/**
	 * CSS selector to find the target element in the DOM.
	 * Used by useMountToHtmlElement to portal the spotlight near the target.
	 */
	targetSelector: string;
	/**
	 * Placement of the popover relative to the target.
	 */
	popoverPlacement: Placement;
	onNext?: () => Promise<void>;
};

const SPOTLIGHT_STEPS_CONFIG: Record<StepId, NewStepDefinition> = {
	'interactive-video': {
		heading: 'interactiveVideoTitle',
		description: 'interactiveVideoDescription',
		lightImage: InteractiveVideoImage,
		darkImage: InteractiveVideoDarkImage,
		targetSelector: '[data-twc-spotlight="linked-video-player"]',
		popoverPlacement: 'right-start',
	},
	documentation: {
		heading: 'documentationTitle',
		description: 'documentationDescription',
		lightImage: DocumentationImage,
		darkImage: DocumentationDarkImage,
		targetSelector: '[data-twc-spotlight="confluence-linked-pages"]',
		popoverPlacement: 'right-start',
		onNext: async (): Promise<void> => {
			const smartLinksContainer = globalThis.document.querySelector<HTMLDivElement>(
				'[data-testid="smart-links-container-layered-link"]',
			);

			if (smartLinksContainer) {
				const mouseEvent = new MouseEvent('click', {
					view: window,
					bubbles: true,
					cancelable: true,
					buttons: 1,
				});
				smartLinksContainer.dispatchEvent(mouseEvent);
			}
		},
	},
} as const;

export interface TWCSpotlightCardTourDependencies {
	useMountToHtmlElement: typeof useMountToHtmlElement;
}

const DEFAULT_DEPENDENCIES: TWCSpotlightCardTourDependencies = {
	useMountToHtmlElement,
};

const scrollTargetIntoView = (selector: string) => {
	const targetElement = globalThis.document?.querySelector<HTMLElement>(selector);
	if (targetElement) {
		targetElement.scrollIntoView({
			behavior: 'smooth',
			block: 'center',
			inline: 'nearest',
		});
	}
};

interface SpotlightStepProps {
	useMountToHtmlElement: TWCSpotlightCardTourDependencies['useMountToHtmlElement'];
	stepDefinition: NewStepDefinition;
	dismiss: () => void;
	onPrimaryAction: () => void;
	primaryActionLabel: React.ReactNode;
	stepIndex: number;
	totalSteps: number;
}

const SpotlightStep = ({
	useMountToHtmlElement: useMountToHtmlElementDependency,
	stepDefinition,
	dismiss,
	onPrimaryAction,
	primaryActionLabel,
	stepIndex,
	totalSteps,
}: SpotlightStepProps) => {
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

	useEffect(() => {
		fireAnalyticsEvent({
			eventType: 'screen',
			name: 'twcSpotlightTourStep',
			attributes: { stepIndex, totalSteps },
		});
	}, [fireAnalyticsEvent, stepIndex, totalSteps]);

	const renderInline = useMountToHtmlElementDependency({
		selectors: stepDefinition.targetSelector,
		as: 'inline',
		position: 'inside',
		observeRoot: globalThis.document?.body as Element | undefined,
	});

	return renderInline(
		<PopoverProvider>
			<PopoverTarget>
				<div />
			</PopoverTarget>
			<PopoverContent
				dismiss={dismiss}
				placement={stepDefinition.popoverPlacement}
				isVisible={true}
			>
				<SpotlightCard testId="spotlight">
					<SpotlightHeader>
						<SpotlightHeadline>
							<FormattedMessage {...messages[stepDefinition.heading]} />
						</SpotlightHeadline>
						<SpotlightControls>
							<SpotlightDismissControl />
						</SpotlightControls>
					</SpotlightHeader>
					<SpotlightMedia>
						<Image
							src={stepDefinition.lightImage}
							srcDark={stepDefinition.darkImage}
							width={SPOTLIGHT_CARD_WIDTH}
							alt=""
						/>
					</SpotlightMedia>
					<SpotlightBody>
						<FormattedMessage {...messages[stepDefinition.description]} />
					</SpotlightBody>
					<SpotlightFooter>
						<SpotlightStepCount>
							<FormattedMessage
								{...messages.progressIndicator}
								values={{
									currentStep: stepIndex + 1,
									totalSteps,
								}}
							/>
						</SpotlightStepCount>
						<SpotlightActions>
							<SpotlightPrimaryAction onClick={onPrimaryAction}>
								{primaryActionLabel}
							</SpotlightPrimaryAction>
						</SpotlightActions>
					</SpotlightFooter>
				</SpotlightCard>
			</PopoverContent>
		</PopoverProvider>,
	);
};

export interface TWCSpotlightCardTourProps {
	dependencies?: TWCSpotlightCardTourDependencies;
	tour: MessageTemplateContext['tour'];
	onTourSkip: () => void;
	onTourComplete: () => void;
}

export const TWCSpotlightCardTour: React.FC<TWCSpotlightCardTourProps> = ({
	dependencies = DEFAULT_DEPENDENCIES,
	tour = DEFAULT_TOUR,
	onTourSkip,
	onTourComplete,
}) => {
	const { messageClicked } = useMessageEvent();
	const { onStepChanged } = usePostOfficeUserInteractions();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const { currentStep, totalSteps, next } = useStepContext();

	const currentStepConfig = tour?.steps[currentStep];

	const handleNext = useCallback(async () => {
		if (!currentStepConfig?.stepId) return;

		const { stepId } = currentStepConfig;
		const stepDefinition = SPOTLIGHT_STEPS_CONFIG[stepId as StepId];
		const isLastStep = currentStep === totalSteps - 1;

		void messageClicked();
		if (!isLastStep) {
			void onStepChanged({
				interactionIdentifier: 'next-step',
				direction: 'next',
				fromStep: currentStep,
				toStep: currentStep + 1,
				stepName: stepId,
				additionalMetadata: { stepId },
			});
		}

		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'twcSpotlightNextStepButton',
			attributes: {
				stepId: currentStepConfig?.stepId || 'unknown',
				fromStep: currentStep,
				toStep: currentStep + 1,
			},
		});

		next();
		if (stepDefinition?.onNext) {
			await stepDefinition.onNext();
		}

		if (isLastStep) {
			onTourComplete();
		} else {
			const nextStepConfig = tour?.steps[currentStep + 1];
			if (nextStepConfig) {
				const nextStepDef = SPOTLIGHT_STEPS_CONFIG[nextStepConfig.stepId as StepId];
				if (nextStepDef) {
					scrollTargetIntoView(nextStepDef.targetSelector);
				}
			}
		}
	}, [
		messageClicked,
		onStepChanged,
		next,
		currentStep,
		totalSteps,
		currentStepConfig?.stepId,
		tour?.steps,
		fireAnalyticsEvent,
		onTourComplete,
	]);

	if (!currentStepConfig) {
		return null;
	}

	const stepDefinition = SPOTLIGHT_STEPS_CONFIG[currentStepConfig.stepId as StepId];

	if (!stepDefinition) {
		return null;
	}

	const isLastStep = currentStep === totalSteps - 1;
	const primaryActionLabel = isLastStep ? (
		<FormattedMessage {...messages.exploreButton} />
	) : (
		<FormattedMessage {...messages.nextButton} />
	);

	return (
		<SpotlightStep
			key={currentStepConfig.stepId}
			useMountToHtmlElement={dependencies.useMountToHtmlElement}
			stepDefinition={stepDefinition}
			dismiss={onTourSkip}
			onPrimaryAction={handleNext}
			primaryActionLabel={primaryActionLabel}
			stepIndex={currentStep}
			totalSteps={totalSteps}
		/>
	);
};
